<template>
	<div class="wrapper">
		<MeetingAlert />

		<div class="active-speaker-label" :class="{ active: activeName }">
			<ActivelySpeakingIcon active class="active-speaker-icon" />
			{{ activeName }}
		</div>

		<div
			class="container"
			ref="container"
			:class="{ gallery: isGalleryView, 'active-speaker': isActiveSpeakerView }"
		>
			<div class="participant-focused" :class="{ hidden: isGalleryView }" />

			<div class="participant-list" v-if="renderReady">
				<!-- This teleport only takes effect for the one focused stream if in active speaker views
				See: https://vuejs.org/guide/built-ins/teleport.html#disabling-teleport -->
				<Teleport
					to=".participant-focused"
					v-for="participant in participants"
					:key="participant.id"
					:disabled="isGalleryView || focusedId !== participant.id"
				>
					<Stream
						@pin-stream="pinStream"
						@element="handleStreamElement($event, participant)"
						:participant="participant"
						:active="participant.id === activeId"
						:pinned="pinnedId === participant.id"
						:profileWidth="profileWidth"
					/>
				</Teleport>
			</div>
		</div>
	</div>
</template>

<script>
import debounce from 'debounce'
import Stream from './Stream'
import {
	getStreamsForDisplay,
	getSharingParticipants,
	getHostParticipants,
	getIsPublishing,
	getConnectionIds,
	getUserByConnectionId,
} from './data'
import MeetingAlert from './MeetingAlert'
import { ActivelySpeakingIcon } from '@/icons'
import { showAlert, hideAlert } from './MeetingAlert'

const WIDTH_DIFF = -4
const RATIO = 0.5625 // 16:9
const PROFILE_PIC_RATIO = 0.18
const PROFILE_PIC_MAX = 90

export const views = {
	GALLERY: 'gallery',
	ACTIVE_SPEAKER: 'active_speaker',
}

function calcItemStyle(width) {
	return {
		width: `${width}%`,
		paddingTop: `${RATIO * width}%`,
	}
}

function isOverflown(elem) {
	if (!elem) return false
	return (
		elem.scrollHeight > elem.clientHeight || elem.scrollWidth > elem.clientWidth
	)
}

function clearInlineStyles(itemElements) {
	itemElements.forEach((elem) => {
		elem.style.removeProperty('width')
		elem.style.removeProperty('padding-top')
	})
}

function setItemStyles(itemElements, width) {
	const style = calcItemStyle(width)
	itemElements
		.filter((elem) => elem)
		.forEach((elem) => Object.assign(elem.style, style))
}

function setItemWidthsUntilNoOverflow(container, itemElements = []) {
	// set initial widths to 100% for every item
	let widthPercentage = 100
	setItemStyles(itemElements, widthPercentage)
	while (isOverflown(container)) {
		// iteratively reduce width by WIDTH_DIFF% until no longer overflowing
		widthPercentage += WIDTH_DIFF
		setItemStyles(itemElements, widthPercentage)
	}
}

function canFocus(idToFocus) {
	return idToFocus && document.getElementById(idToFocus)
}

export default {
	name: 'Streams',
	components: { Stream, MeetingAlert, ActivelySpeakingIcon },
	props: {
		view: {
			type: String,
			validator: (view) => Object.values(views).includes(view),
		},
	},
	data() {
		return {
			pinnedId: null,
			pinnedTime: null,
			focusedId: null,
			galleryObserver: null,
			waitingAlert: null,
			waitingReminderTimer: null,
			stoppedWaiting: false,
			profileWidth: 0,
			lastActiveId: null,
			streamElementsById: {},
			activeParticipant: {},
			renderReady: false,
		}
	},

	emits: ['change-view'],

	mounted() {
		this.$onEventBus({
			streamUpdate: debounce(() => {
				this.updateStreamPositions()
				this.determineFocusedUser()
			}, 100),
			screenStream: debounce(async () => {
				this.switchToActiveSpeakerView()
				await this.$nextTick()
				this.determineFocusedUser()
			}, 750),
			streamDestroyed: (stream) => {
				if (!stream) return
				if (stream.id === this.activeParticipant?.activeStreamId) {
					this.activeName = null
				}
				if (stream.id === this.pinnedId) {
					this.pinnedId = null
				}
				if (stream.id === this.focusedId) {
					this.focusedId = null
					this.determineFocusedUser()
				}
			},
			participantUpdate: (participant) => {
				if (!participant) {
					this.activeParticipant = {}
					return null
				}
				if (participant.id === this.activeParticipant?.id) {
					if (!participant.speaking || !participant.stream?.hasAudio) {
						this.activeParticipant = {}
					}
				}
				if (participant.speaking) {
					this.activeParticipant = participant
				}
			},
		})

		this.galleryObserver = new ResizeObserver(
			debounce(() => {
				if (!this.isGalleryView) return
				requestAnimationFrame(() => {
					this.renderGallery()
				})
			}, 25)
		)
		this.galleryObserver.observe(this.$refs.container)

		this.$nextTick(() => {
			// if we don't wait to render the Teleport, then we get the following error:
			// [Vue warn]: Failed to locate Teleport target with selector ".participant-focused".
			this.renderReady = true
		})

		this.updateStreamPositions()
	},

	methods: {
		async renderGallery(checkCount = 0) {
			const elements = Object.values(this.streamElementsById || {})
			// if (!this.$refs.streams && checkCount < 10) {
			if (!elements.length && checkCount < 10) {
				// we have received stream data but vue hasn't
				// rendered them yet, so we will wait until ready
				setTimeout(() => {
					// use checkCount to avoid infinite recursive loop
					this.renderGallery(checkCount + 1)
				}, 250)
				return
			}
			if (!elements.length) {
				// if we still don't have streams, just bail
				return
			}
			setItemWidthsUntilNoOverflow(this.$refs.container, elements)
			await this.$nextTick()
			this.calculateProfilePicWidth()
		},

		calculateProfilePicWidth() {
			const [first] = Object.values(this.streamElementsById || {})
			if (first) {
				const width = Math.round(first.clientWidth * PROFILE_PIC_RATIO)
				this.profileWidth = width <= PROFILE_PIC_MAX ? width : PROFILE_PIC_MAX
			}
		},

		clearGalleryStyles() {
			const elements = Object.values(this.streamElementsById || {})
			if (elements.length) {
				clearInlineStyles(elements)
			}
		},

		pinStream(streamId) {
			if (streamId && this.pinnedId === streamId) {
				// user was already pinned, so we'll just unpin them
				// and move to whoever was last speaking
				this.pinnedId = null
				this.pinnedTime = null
				this.focusStream(this.lastActiveId)
				return
			}
			this.switchToActiveSpeakerView()
			this.pinnedId = streamId
			this.pinnedTime = Date.now()
			this.focusStream(streamId)
		},

		focusStream(id) {
			if (this.isGalleryView || !canFocus(id)) return
			this.focusedId = id
		},

		switchToActiveSpeakerView() {
			this.clearGalleryStyles()
			this.$emit('change-view', views.ACTIVE_SPEAKER)
		},

		updateStreamPositions() {
			if (this.isGalleryView) {
				this.renderGallery()
			}
		},

		determineFocusedUser() {
			if (!this.isActiveSpeakerView) return
			// Preference hierarchy is as follows:
			//  1) A pinned stream, if pinned more recently than latest screenshare
			//  2) A new screenshare stream
			//  3) Any pinned stream
			//  4) The active speaker's stream, if there is one
			//  5) Leave currently focused stream as focused, if still valid
			//  6) The host's stream, if there is one
			//  7) Any stream other than mine
			//  8) My stream
			const myId = 'publish'
			const [sharer] = getSharingParticipants().reverse() // get most recent screenshare
			if (canFocus(this.pinnedId)) {
				if (!sharer || sharer.stream.creationTime < this.pinnedTime) {
					// if no one is sharing or we pinned more recently than the share
					return this.focusStream(this.pinnedId)
				}
			}
			if (sharer && canFocus(sharer.id)) {
				// if no pin and someone is screensharing, focus on them
				return this.focusStream(sharer.id)
			}
			if (canFocus(this.activeId) && this.activeId !== myId) {
				// if someone other than me is speaking, focus on them
				return this.focusStream(this.activeId)
			}
			if (canFocus(this.focusedId) && this.focusedId !== myId) {
				// just leave focus on the currently focused stream
				return
			}
			const hosts = getHostParticipants().filter((p) => p?.streams?.camera)
			for (const host of hosts) {
				if (canFocus(host.streams.camera.id)) {
					// if a host is streaming, focus on them
					return this.focusStream(host.streams.camera.id)
				}
			}
			// if someone other than us is streaming, then focus on them
			const participant = this.participants.find(
				(p) => canFocus(p.id) && p.id !== myId
			)
			if (participant) {
				return this.focusStream(participant.id)
			}
			// as a last resort, focus on me
			if (canFocus(myId)) {
				return this.focusStream(myId)
			}
		},

		waitingReminder() {
			if (this.waitingAlert) {
				this.waitingAlert = showAlert({
					text: this.$t('meetings_publisher_status_waiting_title'),
					subText: this.$t('meetings_publisher_status_waiting_subtitle'),
					autoHide: false,
				})
			}
		},

		handleStreamElement(element, participant) {
			this.streamElementsById[participant.id] = element
		},
	},

	watch: {
		async view() {
			if (this.isGalleryView) {
				this.focusedId = null
				this.pinnedId = null
				await this.$nextTick()
				this.renderGallery()
			} else {
				this.clearGalleryStyles()
				await this.$nextTick()
				if (!this.focusedId) {
					this.determineFocusedUser()
				}
			}
		},

		activeId(id) {
			if (id) {
				this.determineFocusedUser()
			}
		},

		'participants.length': {
			async handler() {
				await this.$nextTick()
				this.updateStreamPositions()
			},
			deep: true,
		},

		isWaiting: function (waiting) {
			if (waiting && !this.stoppedWaiting) {
				this.waitingAlert = showAlert({
					text: this.$t('meetings_publisher_status_waiting_title'),
					autoHide: false,
				})

				this.waitingReminderTimer = setTimeout(this.waitingReminder, 10000)
			} else {
				if (this.waitingAlert) {
					this.stoppedWaiting = true
					clearTimeout(this.waitingReminderTimer)
					hideAlert(this.waitingAlert)
					this.waitingAlert = null
				}
			}
		},
	},

	computed: {
		isGalleryView() {
			return this.view === views.GALLERY
		},
		isActiveSpeakerView() {
			return this.view === views.ACTIVE_SPEAKER
		},
		participants() {
			const streams = getStreamsForDisplay()
			const isDifferent =
				streams.length !== this.prevStreams?.length ||
				streams.some((s, i) => s.id !== this.prevStreams?.[i]?.id)
			if (isDifferent) {
				this.prevStreams = streams
				return streams
			}
			return this.prevStreams || []
		},
		activeId() {
			if (!this.activeParticipant?.id) return null
			const {
				id,
				connection,
				activeStreamId,
				isPublishing,
			} = this.activeParticipant
			return isPublishing ? id : `${connection.id}_${activeStreamId}`
		},
		activeName() {
			const user = getUserByConnectionId(this.activeParticipant?.connection?.id)
			return user ? user.fullname : null
		},
		isWaiting() {
			return getConnectionIds().length < 2 && getIsPublishing()
		},
		isPublishing() {
			return getIsPublishing()
		},
	},
	beforeUnmount() {
		this.galleryObserver.disconnect()
	},
}
</script>

<style scoped lang="scss">
@import 'src/sass/variables';

$gap: 16px;
$half-gap: 8px;

.wrapper {
	display: flex;
	flex-direction: column;
}

.container {
	flex: 1;
	overflow: hidden;
	position: relative;
	height: 100%;

	.focused-user-name {
		position: absolute;
		top: $gap;
		left: $gap;
		z-index: 1;
		color: white;
	}

	&.gallery {
		:deep(.participant-list) {
			flex-wrap: wrap;
			justify-content: center;
			align-content: center;
			align-items: flex-start;
			height: 100%;
		}
		:deep(.participant) {
			margin: 12px 0 0 12px;
		}
	}

	&.active-speaker {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-direction: column;
		:deep(.participant-focused) {
			position: relative;
			width: 100%;
			flex-grow: 1;
			margin-bottom: $gap;
			background: #222;
			.participant {
				width: calc(100% - 2px);
				height: calc(100% - 2px);
				margin: 1px 0 0 1px;
				position: absolute;
			}
			.profile-pic-avatar {
				--size: 90px !important;
			}
			.participant-pin.pinned {
				opacity: 1;
			}
			.participant-pin {
				div {
					display: block;
				}
			}
		}
		:deep(.participant-list) {
			display: block;
			overflow: auto;
			white-space: nowrap;
			text-align: center;
			max-width: 100%;
			.participant-audio-only .profile-pic {
				margin-top: 20px;
				margin-bottom: 5px;
			}
		}
		:deep(.participant) {
			$height: 120px;
			display: inline-block;
			width: calc(#{$height} / 0.5625); // 16:9 aspect ratio
			padding-top: $height;
			height: 0;
			margin-right: $gap;
			&:last-child {
				margin-right: 0;
			}
		}
		:deep(.profile-pic-avatar) {
			--size: 50px !important;
		}
	}
}

:deep(.participant-list) {
	display: flex;
	width: 100%;
}

:deep(.active-speaker-label) {
	display: flex;
	align-items: center;
	font-size: 12px;
	line-height: 12px;
	opacity: 0;
	transition: opacity ease 0.2s;
	margin-bottom: 8px;
	.active-speaker-icon {
		margin-right: 8px;
	}
	&.active {
		opacity: 1;
	}
}
</style>
